import { useNode } from "@craftjs/core";
import { useEditorStore } from "@state/editor";
import { useThemeStore } from "@state/theme";
import { TextField, Switch, FormGroup, FormControlLabel } from "@mui/material";
import { EventFiltersSettings } from "../EventFilterSettings";
import { BasicCarouselPreviewComponent } from "@components/carousel/basic/BasicCarouselPreviewComponent";

interface BannerBuilderComponentProps {
  title: string;
  description?: string;
  showActionButton?: boolean;
  actionButtonText?: string;
  backgroundColor?: string;
  backgroundImageUrl?: string | null;
  displayEvents?: boolean;
  textColor?: string;
  categoryIDs?: string[];
  eventStatus?: string;
  keywords?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  sizing?: {
    height: {
      value: string;
      previewVh: string;
    };
  };
}

const BannerBuilderComponent = (props: BannerBuilderComponentProps) => {
  const {
    connectors: { connect, drag },
  } = useNode();

  return (
    <BannerPreviewComponent
      {...props}
      connect={connect}
      drag={drag}
    />
  );
};

export default BannerBuilderComponent;

interface BannerPreviewComponentProps extends BannerBuilderComponentProps {
  connect?: any;
  drag?: any;
}

export const BannerPreviewComponent = ({
  title,
  description,
  showActionButton,
  actionButtonText,
  backgroundColor,
  backgroundImageUrl,
  textColor,
  displayEvents,
  categoryIDs,
  eventStatus,
  keywords,
  startDate,
  endDate,
  connect,
  drag,
  sizing,
}: BannerPreviewComponentProps) => {
  const accentColor = useThemeStore(({ accentColor }) => accentColor);
  const isPreview = useEditorStore(({ isPreview }) => isPreview);

  return (
    <div
      ref={(ref: any) => (connect && drag ? connect(drag(ref)) : undefined)}
      className={`min-h-min
        ${backgroundImageUrl && "relative flex flex-col items-start justify-end"}`}
      style={{
        backgroundColor,
        color: textColor,
        height: isPreview ? sizing?.height.previewVh : sizing?.height.value,
      }}
    >
      {backgroundImageUrl && (
        <img
          src={backgroundImageUrl}
          alt="Banner Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      <div className={`py-4 ${displayEvents && "pb-6"} w-full ${backgroundImageUrl && "bg-black/50 z-[1]"}`}>
        <h1 className="ml-4 text-2xl font-bold leading-tight">{title}</h1>
        <div className="ml-4 flex flex-row gap-4">
          {description && <p className="text-base font-semibold">{description}</p>}
          {showActionButton && (
            <div
              className="flex flex-row items-center font-semibold cursor-pointer"
              style={{ color: accentColor }}
            >
              <p>{actionButtonText}</p>
              {/* SVG Arrow */}
              <svg
                className="w-6 h-6"
                viewBox="0 0 24 24"
                height="24"
                width="24"
                role="img"
                aria-hidden="true"
              >
                <title>Link Arrow</title>
                <path
                  stroke="currentColor"
                  strokeWidth="2"
                  d="M9.5 17.5l5-5-5-5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </div>
          )}
        </div>

        {displayEvents && (
          <BasicCarouselPreviewComponent
            title=""
            noPadding
            showSeeMore={false}
            categoryIDs={categoryIDs}
            eventStatus={eventStatus}
            keywords={keywords}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </div>
    </div>
  );
};

const BannerSettings = () => {
  const {
    actions: { setProp },
    title,
    description,
    showActionButton,
    actionButtonText,
    backgroundColor,
    backgroundImageUrl,
    textColor,
    displayEvents,
  } = useNode((node) => ({
    title: node.data.props.title,
    description: node.data.props.description,
    showActionButton: node.data.props.showActionButton,
    actionButtonText: node.data.props.actionButtonText,
    backgroundColor: node.data.props.backgroundColor,
    backgroundImageUrl: node.data.props.backgroundImageUrl,
    textColor: node.data.props.textColor,
    displayEvents: node.data.props.displayEvents,
  }));

  const handleBackgroundColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProp((prop: any) => (prop.backgroundColor = e.target.value));
  };

  const handleTextColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProp((prop: any) => (prop.textColor = e.target.value));
  };

  return (
    <div className="flex flex-col gap-4">
      <TextField
        fullWidth
        label="Title"
        value={title}
        onChange={(e) => setProp((prop: any) => (prop.title = e.target.value))}
      />
      <TextField
        fullWidth
        label="Description"
        value={description}
        onChange={(e) => setProp((prop: any) => (prop.description = e.target.value))}
        multiline
        rows={4}
      />
      <TextField
        id="background-color"
        name="backgroundColor"
        label="Background Color"
        type="color"
        variant="outlined"
        value={backgroundColor || "#ffffff"}
        onChange={handleBackgroundColorChange}
      />
      <TextField
        id="text-color"
        name="textColor"
        label="Text Color"
        type="color"
        variant="outlined"
        value={textColor || "#000000"}
        onChange={handleTextColorChange}
      />

      <TextField
        fullWidth
        label="Background Image URL"
        value={backgroundImageUrl || ""}
        onChange={(e) => setProp((prop: any) => (prop.backgroundImageUrl = e.target.value))}
      />

      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={showActionButton}
              color="primary"
              onChange={(e) => setProp((prop: any) => (prop.showActionButton = e.target.checked))}
            />
          }
          label="Show Action Button"
        />
      </FormGroup>
      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={displayEvents}
              color="primary"
              onChange={(e) => setProp((prop: any) => (prop.displayEvents = e.target.checked))}
            />
          }
          label="Display Events"
        />
      </FormGroup>

      {showActionButton && (
        <TextField
          fullWidth
          label="Action Button Text"
          value={actionButtonText}
          onChange={(e) => setProp((prop: any) => (prop.actionButtonText = e.target.value))}
        />
      )}

      {(showActionButton || displayEvents) && (
        <EventFiltersSettings
          includeCategories={true}
          includeEventStatus={true}
          includeKeywords={true}
          includeDateRange={true}
        />
      )}
    </div>
  );
};

BannerBuilderComponent.craft = {
  props: {
    title: "Your Title Here",
    description: "",
    showActionButton: false,
    actionButtonText: "Learn More",
    backgroundColor: "#ffffff",
    textColor: "#000000",
    backgroundImageUrl: "https://placehold.co/600x400",
    categoryIDs: [],
    eventStatus: null,
    keywords: [],
    startDate: null,
    endDate: null,
    isResizable: true,
    sizing: {
      height: {
        value: "300px",
        previewVh: "30vh",
      },
    },
  },
  related: {
    settings: BannerSettings,
  },
  displayName: "Banner",
};
